import { useEffect, useRef } from 'react';

// 모바일(Capacitor/Android)에서 다이얼로그가 열려 있는 동안 뒤로가기 버튼을 누르면
// 다이얼로그만 닫히고 화면 자체는 안 바뀌게 한다.
//
// 동작: 다이얼로그가 열릴 때 history state 하나를 쌓는다. 뒤로가기(popstate)가 오면 "맨 위에 열린"
// 다이얼로그 하나만 닫는다. 닫기 버튼 등으로 직접 닫힐 때는 쌓아 둔 state를 지우려고 history.back()을
// 부르는데, 그때 오는 popstate는 비동기라 "그사이 새로 열린 다른 다이얼로그"를 닫아 버릴 수 있다
// (2026-10-19 운영: 에이전트 상세 → [수정]이 상세를 닫고 수정 창을 여는 순간, 상세가 부른 back()의
// popstate가 막 열린 수정 창을 닫아 수정·삭제로 못 들어감). 그래서 리스너는 모듈에 하나만 두고,
// 직접 닫으며 부른 back()의 popstate는 skipPops로 세어서 무시한다.

const stack = []; // 열린 다이얼로그들(나중에 열린 게 뒤). 각 항목: { onCloseRef }
let skipPops = 0; // 직접 닫으며 부른 history.back()이 만들 popstate 개수 - 이만큼은 무시
let listening = false;

function handlePopState() {
  if (skipPops > 0) {
    skipPops -= 1;
    return;
  }
  const top = stack.pop();
  if (top) {
    top.closedByBack = true;
    top.onCloseRef.current?.();
  }
}

function ensureListener() {
  if (!listening) {
    window.addEventListener('popstate', handlePopState);
    listening = true;
  }
}

export function useCloseOnBack(open, onClose) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;

    ensureListener();
    const entry = { onCloseRef, closedByBack: false };
    stack.push(entry);
    window.history.pushState({ __closeOnBack: true }, '');

    return () => {
      const idx = stack.indexOf(entry);
      if (idx >= 0) stack.splice(idx, 1);
      if (!entry.closedByBack) {
        // 뒤로가기가 아니라 다이얼로그 쪽에서 직접 닫힌 경우 - 쌓아 둔 state를 되돌리고,
        // 그 back()이 만드는 popstate는 다른 다이얼로그를 닫지 않게 무시한다.
        skipPops += 1;
        window.history.back();
      }
    };
    // open 변화에만 반응한다 - onClose identity가 바뀌어도 push/cleanup을 다시 돌리지 않는다(ref로 흡수).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}

// 테스트에서 모듈 상태를 초기화할 때만 쓴다.
export function __resetCloseOnBackForTest() {
  stack.length = 0;
  skipPops = 0;
}

export default useCloseOnBack;
